import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../utils/axios";

/**
 * Fetch Salary Head Master list
 */
export const fetchSalaryHeadMaster = createAsyncThunk(
    'payroll/fetchSalaryHeadMaster',
    async (limit: number = 200): Promise<any[]> => {
        const res = await axios.get(`/api/v1/payroll/salaryHead?limit=${limit}`);
        return res.data?.data || [];
    }
);

/**
 * Fetch Employee Salary Config (latest by effectiveFrom DESC)
 */
export const fetchEmployeeSalaryConfig = createAsyncThunk(
    'payroll/fetchEmployeeSalaryConfig',
    async ({ employeeId, limit = 10, sortLatest = false }: { employeeId: string; limit?: number; sortLatest?: boolean }): Promise<any[]> => {
        const sortParam = sortLatest ? `&sort=-effectiveFrom&_t=${Date.now()}` : '';
        const res = await axios.get(`/api/v1/payroll/employeeSalaryConfig?employee=${employeeId}&limit=${limit}${sortParam}`);
        return res.data?.data || [];
    }
);

/**
 * Fetch Salary Grade by ID
 */
export const fetchSalaryGradeById = createAsyncThunk(
    'payroll/fetchSalaryGradeById',
    async (gradeId: string): Promise<any> => {
        const res = await axios.get(`/api/v1/payroll/salaryGrade/${gradeId}`);
        return res.data?.data || res.data;
    }
);

/**
 * Fetch Employee Timesheet Monthly
 */
export const fetchEmployeeTimesheetMonthly = createAsyncThunk(
    'payroll/fetchEmployeeTimesheetMonthly',
    async ({ employeeId, year, month }: { employeeId: string; year: number; month: number }): Promise<any> => {
        const res = await axios.get(`/api/v1/employee/timesheet/monthly/${employeeId}`, {
            params: { year, month },
            headers: { 'x-silent': '1' }
        });
        return res.data;
    }
);

/**
 * Fetch Employee Leave Requests (approved/pending/rejected) — same source as the
 * mobile app calendar. Returns raw leave requests each with dates[], status, leaveConfig.
 */
export const fetchEmployeeLeaveRequests = createAsyncThunk(
    'payroll/fetchEmployeeLeaveRequests',
    async ({ employeeId }: { employeeId: string }): Promise<any[]> => {
        const res = await axios.get(`/api/v1/leaves/request/employee/${employeeId}`, {
            headers: { 'x-silent': '1' }
        });
        return res.data?.data || [];
    }
);

/**
 * Fetch Employee Daily Attendance (fallback)
 */
export const fetchEmployeeDailyAttendance = createAsyncThunk(
    'payroll/fetchEmployeeDailyAttendance',
    async ({ accountId, startDate, endDate, employeeId }: { accountId: string; startDate: string; endDate: string; employeeId: string }): Promise<any> => {
        const res = await axios.get(`/api/v1/employee/dailyAttendance`, {
            params: { accountId, startDate, endDate, employee: employeeId },
            headers: { 'x-silent': '1' }
        });
        return res.data;
    }
);

/**
 * Fetch Holidays in date range
 */
export const fetchHolidaysInRange = createAsyncThunk(
    'payroll/fetchHolidaysInRange',
    async ({ startDate, endDate }: { startDate: string; endDate: string }): Promise<any> => {
        const res = await axios.get(`/api/v1/holidays`, {
            params: { dateFrom: startDate, dateTo: endDate, isActive: true },
            headers: { 'x-silent': '1' }
        });
        return res.data;
    }
);

/**
 * Fetch Weekly Timesheets (paginated list when year/month omitted)
 */
export const fetchEmployeeWeeklyTimesheets = createAsyncThunk(
    'payroll/fetchEmployeeWeeklyTimesheets',
    async ({ employeeId, page = 1, limit = 50 }: { employeeId: string; page?: number; limit?: number }): Promise<any> => {
        const res = await axios.get(`/api/v1/employee/timesheet/weekly/${employeeId}`, {
            params: { page, limit },
            headers: { 'x-silent': '1' }
        });
        return res.data;
    }
);

/**
 * Fetch a single Weekly Timesheet with full day-wise breakdown
 */
export const fetchEmployeeWeeklyTimesheetDetail = createAsyncThunk(
    'payroll/fetchEmployeeWeeklyTimesheetDetail',
    async ({ employeeId, year, month, week }: { employeeId: string; year: number; month: number; week?: number }): Promise<any> => {
        const res = await axios.get(`/api/v1/employee/timesheet/weekly/${employeeId}`, {
            params: week !== undefined ? { year, month, week } : { year, month },
            headers: { 'x-silent': '1' }
        });
        return res.data;
    }
);

/**
 * Approve / Reject / Reopen a Weekly Timesheet
 * action: 'approve' | 'reject' | 'pending'
 */
export const approveEmployeeWeeklyTimesheet = createAsyncThunk(
    'payroll/approveEmployeeWeeklyTimesheet',
    async (
        { approverId, employeeId, timesheetId, action, rejectedComments, userRole }:
        { approverId: string; employeeId: string; timesheetId: string; action: 'approve' | 'reject' | 'pending'; rejectedComments?: string; userRole?: string }
    ): Promise<any> => {
        const res = await axios.put(`/api/v1/employee/timesheet/approve/${approverId}`, {
            employeeId,
            timesheetId,
            action,
            rejectedComments,
            userRole,
        });
        return res.data;
    }
);
